import { createContext, ReactNode, useCallback, useContext } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import { UserApi, UserCreateApi, UserListApi, UserUpdateApi } from './UsersApiProviderTypes';

interface UsersApiContextType {
  getUsers: (skip?: number, limit?: number) => Promise<UserListApi>;
  getUserById: (userId: string) => Promise<UserApi>;
  createUser: (data: UserCreateApi) => Promise<UserApi>;
  updateUser: (userId: string, data: Partial<UserUpdateApi>) => Promise<UserApi>;
  deleteUser: (userId: string) => Promise<void>;
  changeUserPassword: (userId: string, password: string) => Promise<void>;
  sendResetPasswordEmail: (email: string) => Promise<void>;
  uploadUserPhoto: (userId: string, photo: string) => Promise<void>;
  deleteUserPhoto: (userId: string) => Promise<void>;
  syncUserPhoto: (userId: string, photo: string, initialPhoto: string) => Promise<boolean>;
}

const UsersApiContext = createContext<UsersApiContextType | null>(null);

export function UsersApiProvider({
  children,
  organizationId,
}: {
  children: ReactNode;
  organizationId?: string;
}) {
  const { get, post, put, patch, del } = useApiService(CONFIG.SERVER_ORGANIZATION, organizationId);

  const getUsers = useCallback(
    async (skip: number = 0, limit: number = 10): Promise<UserListApi> => {
      try {
        const response = await get(`/v1/users?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
      }
    },
    [get],
  );

  const getUserById = useCallback(
    async (userId: string): Promise<UserApi> => {
      try {
        const response = await get(`/v1/users/${userId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching user by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createUser = useCallback(
    async (user: UserCreateApi): Promise<UserApi> => {
      try {
        const response = await post(`/v1/users`, user);
        return response.data;
      } catch (error) {
        console.error('Error creating user:', error);
        throw error;
      }
    },
    [post],
  );

  const changeUserPassword = useCallback(
    async (userId: string, password: string): Promise<void> => {
      try {
        const response = await post(`/v1/users/${userId}/change_password`, password);
        return response.data;
      } catch (error) {
        console.error('Error changing user password:', error);
        throw error;
      }
    },
    [post],
  );

  const sendResetPasswordEmail = useCallback(
    async (email: string): Promise<void> => {
      try {
        const response = await post(`/v1/users/send_reset_password_email`, { email });
        return response.data;
      } catch (error) {
        console.error('Error sending reset password email:', error);
        throw error;
      }
    },
    [post],
  );

  const updateUser = useCallback(
    async (userId: string, user: Partial<UserUpdateApi>): Promise<UserApi> => {
      try {
        const response = await patch(`/v1/users/${userId}`, user);
        return response.data;
      } catch (error) {
        console.error('Error updating user:', error);
        throw error;
      }
    },
    [patch],
  );

  const deleteUser = useCallback(
    async (userId: string): Promise<void> => {
      try {
        const response = await del(`/v1/users/${userId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
      }
    },
    [del],
  );

  const uploadUserPhoto = useCallback(
    async (userId: string, photo: string): Promise<void> => {
      try {
        const response = await put(`/v1/users/${userId}/photo`, { photo });
        return response.data;
      } catch (error) {
        console.error('Error uploading user photo:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteUserPhoto = useCallback(
    async (userId: string): Promise<void> => {
      try {
        const response = await del(`/v1/users/${userId}/photo`);
        return response.data;
      } catch (error) {
        console.error('Error deleting user photo:', error);
        throw error;
      }
    },
    [del],
  );

  const syncUserPhoto = useCallback(
    async (userId: string, photo: string, initialPhoto: string): Promise<boolean> => {
      if (photo.startsWith('data:image')) {
        await uploadUserPhoto(userId, photo);
        return true;
      }
      if (!photo && initialPhoto) {
        await deleteUserPhoto(userId);
        return true;
      }
      return false;
    },
    [uploadUserPhoto, deleteUserPhoto],
  );

  return (
    <UsersApiContext.Provider
      value={{
        getUsers,
        getUserById,
        createUser,
        changeUserPassword,
        sendResetPasswordEmail,
        updateUser,
        deleteUser,
        uploadUserPhoto,
        deleteUserPhoto,
        syncUserPhoto,
      }}
    >
      {children}
    </UsersApiContext.Provider>
  );
}

export function useUsersApi(): UsersApiContextType {
  const context = useContext(UsersApiContext);
  if (!context) {
    throw new Error('useUserApi must be used within a UsersApiProvider');
  }
  return context;
}
